import React, { useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { TournamentActivity } from "@nestcoinco/hyper-api-gateway-api-client";
import { Image } from "react-bootstrap";

import { ROUTES } from "../../utils/constants";
import {
  getWeekRanges,
  formatWeekRangeLabel,
  getMetadataValue,
} from "../../utils/helpers";
import {
  extractOgTitleFromMainTitle,
  renderHeaders,
} from "../../utils/renderHeaders";
import useSubscription from "../../react-query/queries/use-subscription-status";

import DashboardHeader from "../../components/dashboard-page/sections/DashboardHeader/DashboardHeader";
import ResultHistorySection, {
  ResultHistorySectionSkeleton,
  StaticResultHistorySection,
} from "../../components/result-history/ResultHistorySection/ResultHistorySection";
import PrimaryActionButton from "../../components/Buttons/PrimaryActionButton/PrimaryActionButton";
import TrophyIcon from "../../components/icons/TrophyIcon";

import { MOCK_SECTIONS } from "./mock-activities";
import BattleImage from "../../images/Battle.png";
import { useSequentialWeekActivities } from "../../hooks/useSequentialWeekActivities";
import useMarkActivityViewedMutation from "../../react-query/mutations/use-mark-activity-viewed";
import "./ResultPage.scss";

interface UnsubscribedOverlayProps {
  onCtaClick: () => void;
}

function UnsubscribedOverlay({ onCtaClick }: UnsubscribedOverlayProps) {
  const { t } = useTranslation("common");

  return (
    <div className="unsubscribed-overlay">
      <div className="unsubscribed-content">
        <p className="unsubscribed-message">
          {t("results_page.play_everything")}
          {t("punctuations.full_stop")}
          <br />
          {t("games:battle")}
          <Image
            src={BattleImage}
            alt="battle"
            className="inline-battle-image"
          />
          {t("results_page.anyone")}
          {t("punctuations.full_stop")}
        </p>
        <PrimaryActionButton
          text={t("results_page.get_all_access")}
          onClick={onCtaClick}
          buttonClassName="w-100"
        />
      </div>
    </div>
  );
}

function EmptyResultsState() {
  const { t } = useTranslation("common");
  const navigate = useNavigate();

  return (
    <div className="empty-results-state">
      <div className="trophy-container">
        <TrophyIcon color="#7E43FF" width={60} height={60} />
      </div>
      <h3 className="empty-heading">
        {t("results_page.ready_to_make_your_mark")}
      </h3>
      <p className="empty-subtext">
        {t("results_page.complete_a_1v1_game_to_see_your_results")}
      </p>
      <PrimaryActionButton
        text={t("results_page.play_game")}
        onClick={() => navigate(ROUTES.GAMES.ROOT)}
        buttonClassName="w-100"
      />
    </div>
  );
}

function ResultPage() {
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const { t: tMeta } = useTranslation("metadata");
  const {
    gateAction,
    isSubscribed,
    isLoading: isSubscriptionLoading,
  } = useSubscription();

  const isUnsubscribed = isSubscribed === false;

  const weekSections = useMemo(() => {
    const { thisWeek, lastWeek, twoWeeksAgo } = getWeekRanges();
    return [
      { title: t("results_page.this_week"), ...thisWeek },
      { title: t("results_page.last_week"), ...lastWeek },
      {
        title: formatWeekRangeLabel(twoWeeksAgo.startDate, twoWeeksAgo.endDate),
        ...twoWeeksAgo,
      },
    ];
  }, [t]);

  const { sectionQueries, sectionFullyLoaded, isEmpty } =
    useSequentialWeekActivities(
      weekSections,
      !isSubscriptionLoading && !isUnsubscribed
    );

  const revealMutation = useMarkActivityViewedMutation();

  const navigateToResult = useCallback(
    (activity: TournamentActivity) => {
      const tournamentId = getMetadataValue(activity, "tournamentId");
      const gameSlug = getMetadataValue(activity, "gameSlug");
      if (!tournamentId || !gameSlug) return;
      navigate(`${ROUTES.GAMES.ROOT}/${gameSlug}/${tournamentId}`, {
        state: { returnTo: ROUTES.ROOT.RESULTS },
      });
    },
    [navigate]
  );

  const handleReveal = useCallback(
    (activity: TournamentActivity) => {
      revealMutation.mutate(activity.referenceId);
      navigateToResult(activity);
    },
    [revealMutation, navigateToResult]
  );

  const handleActivityClick = useCallback(
    (activity: TournamentActivity) => navigateToResult(activity),
    [navigateToResult]
  );

  const renderContent = () => {
    if (isUnsubscribed) {
      return MOCK_SECTIONS.map(({ title, activities }) => (
        <StaticResultHistorySection
          key={title}
          title={title}
          activities={activities}
        />
      ));
    }

    if (isSubscriptionLoading) {
      return weekSections.map(({ title }) => (
        <ResultHistorySectionSkeleton key={title} />
      ));
    }

    if (isEmpty) {
      return <EmptyResultsState />;
    }

    return weekSections.map(({ title }, i) => {
      const prevFullyLoaded = i === 0 || sectionFullyLoaded[i - 1];
      if (!prevFullyLoaded) return null;
      if (sectionQueries[i].query.isLoading) {
        return <ResultHistorySectionSkeleton key={title} />;
      }
      return (
        <ResultHistorySection
          key={title}
          title={title}
          records={sectionQueries[i].records}
          query={sectionQueries[i].query}
          onActivityClick={handleActivityClick}
          onReveal={handleReveal}
        />
      );
    });
  };

  return (
    <>
      {renderHeaders({
        title: tMeta("sigma_results_title"),
        description: tMeta("sigma_results_description"),
        ogTitle: extractOgTitleFromMainTitle(tMeta("sigma_results_title")),
      })}
      <main className="results-page MobileOnly">
        {isUnsubscribed && (
          <UnsubscribedOverlay onCtaClick={() => gateAction(() => {})} />
        )}
        <div className={`results-content ${isUnsubscribed ? "is-blurred" : ""}`}>
          <DashboardHeader />
          <div className="page-sections">{renderContent()}</div>
        </div>
      </main>
    </>
  );
}

export default ResultPage;
